import axios from "axios";
import { getUuid } from "./functions";
import { client } from "@src/redux/index";
import gql from "graphql-tag";
import toast from "react-hot-toast";

const query = gql`
  mutation getSignedUrl($data: signedUrlInput!) {
    getSignedUrl(data: $data) {
      url
      publicUrl
      filename
      status
      error {
        message
        statusCode
      }
    }
  }
`;
interface File {
  status?: "removed" | "uploading" | "done";
  url?: string;
  message?: string;
  type?: string;
  name?: string;
  size: number;
}

let fileUploadResponse: any | null = null;
let lastUploadRejectionMessage: string | null = null;
export let fileList: any = [];

export const action = fileUploadResponse?.data?.getSignedUrl?.url || "";

// MIME -> file-extension(s). The previous logic derived the extension from
// mime.split("/")[1], which gave ".plain" for text/plain and ".jpeg" for
// image/jpeg — neither matched real filenames, so the extension check failed
// and S3 was asked to sign URLs with bogus extensions.
const MIME_TO_EXTENSIONS: Record<string, string[]> = {
  "image/png": ["png"],
  "image/jpg": ["jpg"],
  "image/jpeg": ["jpg", "jpeg"],
  "image/pdf": ["pdf"],
  "application/pdf": ["pdf"],
  "text/plain": ["txt"],
  "application/vnd.ms-excel": ["xls"],
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": ["xlsx"],
};

// Mirrors api/src/app/helpers/utils.ts get_content_type. Must stay in sync
// with the backend: the signed PutObject URL is signed with whatever
// Content-Type get_content_type returns for the generated filename, so the
// PUT request's Content-Type header MUST equal this value or S3 rejects
// with SignatureDoesNotMatch.
const EXTENSION_TO_CONTENT_TYPE: Record<string, string> = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  pdf: "application/pdf",
  txt: "text/plain",
  xls: "application/vnd.ms-excel",
  xlsx:
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
};

function getFileExtension(name?: string): string {
  if (!name) return "";
  const idx = name.lastIndexOf(".");
  if (idx < 0 || idx === name.length - 1) return "";
  return name.slice(idx + 1).toLowerCase();
}

function contentTypeForFilename(filename?: string): string {
  const ext = getFileExtension(filename);
  return EXTENSION_TO_CONTENT_TYPE[ext] || "application/octet-stream";
}

export const handleFileChanged = ({ file }: {file: any;}) => {
  if (file?.status === "removed") {
    fileList = [];
    return;
  }
  if (file?.status === "done") {
    // handleUpload's onSuccess set file.url and file.s3Key. Nothing to do
    // here. The previous "uploading" branch used to pre-rewrite status to
    // "done" and force file.url to the signed URL — that caused the form to
    // think upload succeeded even when S3 had rejected the PUT, and then
    // decryptOfflineReading would later fail with "key does not exist"
    // because the file was never persisted. We now trust Antd's status.
    fileList = [...fileList];
    return;
  }
  if (file?.status === "error") {
    if (lastUploadRejectionMessage) {
      toast.error(lastUploadRejectionMessage);
      lastUploadRejectionMessage = null;
    } else {
      toast.error("File upload failed.");
    }
  }
};

export const handleBeforeUpload = async (
file: File,
fileType: string,
allowedFileFormat: Array<
  "image/png" |
  "image/jpg" |
  "image/jpeg" |
  "image/pdf" |
  "text/plain" |
  "application/vnd.ms-excel" |
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"> =
[]) =>
{
  const { name, type, size } = file;

  const allowedTypes = allowedFileFormat.length ?
  allowedFileFormat :
  [
  "image/png",
  "image/jpg",
  "image/jpeg",
  "image/pdf",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/plain"];

  const validExtensions = allowedTypes.flatMap(
    (mime) => MIME_TO_EXTENSIONS[mime] || [],
  );

  const fileExtension = getFileExtension(name);
  const maxSizeInMb = 1; // Maximum allowed size in MB
  const isValidExtension =
    fileExtension !== "" && validExtensions.includes(fileExtension);
  const isValidType = allowedTypes.includes(type as typeof allowedTypes[number]);
  const isValidSize = size / 1024 / 1024 <= maxSizeInMb;

  if ((isValidExtension || isValidType) && isValidSize) {
    // Persist the file's REAL extension into the generated S3 key so the
    // backend's get_content_type(filename) maps it to the correct
    // Content-Type. Otherwise the PUT request's Content-Type header doesn't
    // match the signed URL and S3 rejects with SignatureDoesNotMatch.
    const safeExtension = fileExtension || validExtensions[0] || "bin";
    const fileUpload = {
      type: fileType,
      filename: `${getUuid()}.${safeExtension}`,
    };

    lastUploadRejectionMessage = null;
    fileUploadResponse = await client.mutate({
      mutation: query,
      variables: { data: fileUpload }
    });
  } else {
    const friendlyAllowed = validExtensions.length
      ? validExtensions.map((e) => e.toUpperCase()).join(", ")
      : "PNG, JPG, JPEG, PDF, TXT, XLS, XLSX";
    let message = `Allowed formats: ${friendlyAllowed}`;
    if (!isValidSize) {
      message = "File size should not exceed 1MB";
    }
    lastUploadRejectionMessage = message;

    fileUploadResponse = {
      data: {
        getSignedUrl: {
          error: { message },
        },
      },
    };
  }
};

export const handleUpload = ({ onSuccess, onError, file }: any) => {
  const signedUrl = fileUploadResponse?.data?.getSignedUrl?.url;
  const signedFilename = fileUploadResponse?.data?.getSignedUrl?.filename;
  const publicUrl = fileUploadResponse?.data?.getSignedUrl?.publicUrl;
  if (signedUrl) {
    // Use the SAME Content-Type the backend used to sign the URL (derived
    // from the generated filename's extension). Sending file.type causes
    // S3 SignatureDoesNotMatch when the two differ (e.g. file.type is
    // "text/plain" but backend signed as "application/octet-stream"). The
    // PUT then fails and the file never reaches S3 even though Antd may
    // surface a misleading success — which is exactly what produced the
    // "decryptOfflineReading: The specified key does not exist" symptom.
    const putContentType = contentTypeForFilename(signedFilename);
    axios.
    put(signedUrl, file as any, {
      headers: { "Content-Type": putContentType }
    }).
    then(() => {
      // Prefer the URL the backend says the object is readable at. The V3
      // asset buckets are private behind CloudFront, so the presigned URL
      // minus its query — the fallback, kept for older API builds — is an S3
      // URL that 403s when rendered.
      file.url = publicUrl || signedUrl.split("?")[0];
      // Pass the backend-generated S3 key through on the file object so
      // form submit handlers can use it directly instead of trying to
      // parse it back out of the URL (which gets wrong answers for
      // path-style URLs like https://s3.<region>.amazonaws.com/<bucket>/<key>).
      file.s3Key = signedFilename;
      onSuccess?.(null, file);
    }).
    catch((error: any) => {
      onError?.(error, file);
    });
  } else {
    onError?.(
      null,
      fileList?.[0]?.message ||
      fileUploadResponse?.data?.getSignedUrl?.error?.message
    );
  }
};